import { z } from 'zod';

import { assertOk, parseJson } from './response';
import { resolveApiFetch } from './shared';

export type SessionStateCode = 'OK' | 'TENANT_UNASSIGNED' | 'UNAUTHENTICATED';

export interface SessionUserPayload {
  id: string;
  email: string;
  role: string;
}

export interface SessionTenantPayload {
  id: string;
  slug: string;
  name?: string;
}

export interface SessionTenantSummary extends SessionTenantPayload {
  role?: string | null;
  status?: string | null;
  lastUsedAt?: string | null;
  isDemo?: boolean;
}

export interface SessionApiResponse {
  authenticated: boolean;
  user?: SessionUserPayload;
  tenant?: SessionTenantPayload;
  tenants?: SessionTenantSummary[];
  state?: SessionStateCode;
  onboardingStatus?: 'pending' | 'completed';
}

export const SESSION_QUERY_KEY = ['session'];

const SessionUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  role: z.string(),
});

const SessionTenantSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string().optional(),
});

const SessionTenantSummarySchema = SessionTenantSchema.extend({
  role: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  lastUsedAt: z.string().nullable().optional(),
  isDemo: z.boolean().optional(),
});

const SessionApiResponseSchema = z.object({
  authenticated: z.boolean(),
  user: SessionUserSchema.optional(),
  tenant: SessionTenantSchema.optional(),
  tenants: z.array(SessionTenantSummarySchema).optional(),
  state: z.enum(['OK', 'TENANT_UNASSIGNED', 'UNAUTHENTICATED']).optional(),
  onboardingStatus: z.enum(['pending', 'completed']).optional(),
});

export async function fetchSession(): Promise<SessionApiResponse> {
  const apiFetch = await resolveApiFetch();
  const response = await apiFetch('/session', {
    headers: {
      Accept: 'application/json',
    },
  });

  if (response.status === 404) {
    return { authenticated: false };
  }

  await assertOk(response);
  return parseJson(response, SessionApiResponseSchema);
}



